import React from "react";
import { Input, Select } from "antd";
import { NumberSetter } from "./NumberSetter";
import { BooleanSetter } from "./BooleanSetter";
import { JsonSetter } from "./JsonSetter";
import { ColorPickerTrigger } from "./ColorPickerTrigger";
// 操作相关类型
interface OperationAction {
  tooltip: string;
  targetPath?: string; // 用于定位克隆目标
}

export interface Operations {
  actions: {
    clone?: OperationAction;
    visibility?: OperationAction;
    delete?: OperationAction;
  };
}

// JSON Logic相关类型
type JsonLogicVar = { var: string };
type JsonLogicConst = { const: any };
type JsonLogicOperation = { [key: string]: any[] };
type JsonLogicValue = JsonLogicVar | JsonLogicConst | JsonLogicOperation;

interface UpdateStrategy {
  when: string[]; // 触发更新的字段
  keepFields: string[]; // 保持不变的字段
}
// 值联动相关类型
interface EffectConfig {
  dependencies: string[];
  handler: string;
  params: Record<string, JsonLogicValue>;
  updateStrategy?: UpdateStrategy;
}

// 显示联动相关类型
type VisibilityCondition = JsonLogicOperation;

// 选项联动相关类型
interface OptionsMapping {
  source: string;
  dependency: string;
  transform?: {
    map: [JsonLogicValue, Record<string, JsonLogicValue>];
  };
}

export interface Option {
  label: string;
  value: string | number;
}
// Setter配置类型定义
export interface SetterConfig {
  name: string;
  title: string;
  setter: "string" | "number" | "boolean" | "select" | "json" | "colorPicker";
  options?: Option[] | { mapping: OptionsMapping };
  min?: number;
  max?: number;
  step?: number;
  priority?: number;
  effects?: EffectConfig;
  visible?: VisibilityCondition;
}
// 组合型field的定义
export interface CompositeField {
  name: string;
  title: string;
  fields: Field[]; // setter路径数组
  operations?: Operations; // 添加操作配置
}

// field可以是字符串路径或组合配置
export type Field = string | CompositeField;

// Form布局类型
type FormLayout = "horizontal" | "vertical" | "inline";

// 标签对齐方式
type LabelAlign = "left" | "right";

// 表单项布局配置
interface FormItemLayout {
  labelCol?: {
    span?: number;
    offset?: number;
  };
  wrapperCol?: {
    span?: number;
    offset?: number;
  };
}

// 布局项定义
interface LayoutItem {
  name: string;
  title: string;
  fields: Field[];
  operations?: Operations; // 添加操作配置
}

// 增强后的setterLayout定义
export interface setterLayout {
  name: string;
  title: string;
  items: LayoutItem[];
  // 表单布局配置
  formLayout?: {
    layout?: FormLayout; // 表单布局方式
    labelAlign?: LabelAlign; // 标签对齐方式
    labelWidth?: number; // 标签宽度
    labelCol?: FormItemLayout["labelCol"]; // 标签布局
    wrapperCol?: FormItemLayout["wrapperCol"]; // 内容布局
    colon?: boolean; // 是否显示冒号
    requiredMark?: boolean | "optional"; // 必选样式
  };
}
// Setter属性类型定义
interface SetterProps {
  config: SetterConfig;
  value: any;
  onChange: (value: any) => void;
}

// 组件映射表
const SetterComponentMap = {
  string: Input,
  number: NumberSetter,
  boolean: BooleanSetter,
  select: (props: any) => (
    <Select
      {...props}
      options={props.config?.options}
      style={{ width: "100%" }}
    />
  ),
  json: JsonSetter,
  color: ColorPickerTrigger,
};

export const Setter: React.FC<SetterProps> = ({ config, value, onChange }) => {
  const Component = SetterComponentMap[config.setter];
  const { setter, ...restConfig } = config;
  if (!Component) {
    console.warn(`Unsupported setter type: ${config.setter}`);
    return null;
  }

  const handleChange = (newValue: any) => {
    const finalValue = newValue?.target?.value ?? newValue;
    // 处理不同组件的值变化
    if (config.setter === "boolean") {
      onChange(finalValue);
    } else if (config.setter === "json") {
      try {
        const jsonValue = JSON.parse(finalValue);
        onChange(jsonValue);
      } catch (e) {
        console.error("Invalid JSON:", e);
      }
    } else {
      onChange(finalValue);
    }
  };

  // 根据不同的setter类型传递不同的props
  const getSetterProps = () => {
    const baseProps = {
      value: config.setter === "json" ? JSON.stringify(value, null, 2) : value,
      onChange: handleChange,
    };

    switch (config.setter) {
      case "number":
        return {
          ...restConfig,
          ...baseProps,
          // variant: 'input'  // 为number类型设置默认variant
        };
      case "select":
      case "colorPicker":
        return {
          ...baseProps,
          config: {
            options: config.options,
          },
        };
      default:
        return baseProps;
    }
  };

  return <Component {...getSetterProps()} />;
};
